/**
 * 별채대화 데이터 (DEV_SPEC 4.2, D21: 폴링).
 *
 * - 처음: GET /api/room (말 + 최근 3일 매일 콘텐츠 + 생성 중인지)
 * - 폴링: GET /api/room/pending?after=마지막 id. 멤버가 쓰는 중이면 1.2초, 고민 카드를 올린 뒤 1분은 3초,
 *   그 밖에는 15초마다. 창이 가려져 있으면 쉽니다
 * - 내 말: 바로 화면에 「보내는 중」으로 두고, 실패하면 「다시 보내기」. **다시 보낼 때는 같은 clientKey** 라
 *   서버에 두 번 저장되지 않습니다 (시안 03 X1)
 * - 고민 카드: 스레드 글(GET /api/posts/:id)을 따로 불러 카드 상태(읽는 중 → 답 1개 → 답 2개)를 보여 줍니다
 *
 * 응답 리듬 (시안 04 DS-1)
 * - 보내자마자 「입력 중」(D1a). 첫 멤버를 아직 모르면 이모, 서버가 미리 보기 계획의 첫 멤버를 알려 주면 그 멤버
 * - 순차 공개(D1b · C-37 · D100): 폴링 한 번에 멤버 말이 여럿 오거나 앞 말과 너무 붙어 오면, 서버 공개 큐와 같은 공식
 *   (max(앞 말 읽을 시간, 올릴 말 입력 중) 1.5~4초, 같은 멤버 연속 0.8~1.2초 × 말 속도)으로 하나씩 꺼내 보입니다.
 *   말이 보이고 0.4초 뒤부터 다음 멤버 「입력 중」(빈 화면은 그 0.4초뿐)
 * - 기기에 남은 말부터(D1d): 다시 열면 지난번에 받은 말을 바로 그리고, 새로 받는 동안 refreshing
 * - 처음 여는 별채(firstOpen): 입장 인사가 이미 다 쓰여 있어도 첫 멤버 말부터는 같은 간격으로 하나씩 보입니다
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { errorMessageOf, isAbortError } from '../../api/client.js';
import * as roomApi from '../../api/room.js';
import * as boardApi from '../../api/board.js';
import { toFeedPost, toggledReactions } from '../board/boardFeed.js';
import { typingLeadMs } from '@config/timing/reveal.js';
import { currentRevealSpeed, noteRevealSpeed, revealGapOf } from '../../stores/revealSpeed.js';

const FAST_POLL_MS = 1200;
const WATCH_POLL_MS = 3000;
const IDLE_POLL_MS = 15000;
const WATCH_AFTER_WORRY_MS = 60000;
const WORRY_POLL_MS = 4000;
// 보낸 직후 첫 멤버를 모를 때 띄우는 「입력 중」 (D1a)
const DEFAULT_TYPING_MEMBER = 'imo';

/** 이 기기에서 마지막으로 받은 별채대화(앱을 쓰는 동안만, 화면을 다시 열 때 바로 그리기용) */
let roomCache = null;

function newClientKey() {
    return crypto.randomUUID();
}

function mergeMessages(current, incoming) {
    if (!incoming.length) {
        return current;
    }
    const byId = new Map(current.map((message) => [message.roomMessagesId, message]));
    for (const message of incoming) {
        byId.set(message.roomMessagesId, message);
    }
    return [...byId.values()].sort((a, b) => a.roomMessagesId - b.roomMessagesId);
}

/**
 * 처음 여는 별채의 말을 바로 그릴 앞부분(들어왔다는 줄 · 단골 인사)과 하나씩 꺼낼 뒷부분(첫 멤버 말부터)으로 나눕니다
 */
export function splitEntrance(messages) {
    const firstMember = messages.findIndex((message) => message.sender === 'member');
    if (firstMember < 0) {
        return { shown: messages, queued: [] };
    }
    return { shown: messages.slice(0, firstMember), queued: messages.slice(firstMember) };
}

/**
 * @param {object} [options]
 * @param {(incoming: object[]) => void} [options.onArrive]  화면에 새로 보인 말
 * @param {string|number|null} [options.cacheKey]  이 기기 캐시의 주인(유저). 같으면 지난번 말부터 그립니다
 */
export function useRoom({ onArrive, cacheKey = null } = {}) {
    const cached = cacheKey !== null && roomCache?.key === cacheKey ? roomCache.state : null;
    const [state, setState] = useState(
        () =>
            cached ?? {
                status: 'loading',
                error: null,
                messages: [],
                hasMore: false,
                // 카드 말의 글. dailyItemsId → 글 (리액션도 여기서 바꿉니다)
                itemsById: {},
                quiet: false,
                hasBirth: true,
                pushAsk: null,
                returnVisit: null,
                openedLastId: 0,
            }
    );
    // 기기에 남은 말을 그린 채로 새 말을 받는 중 (상단 2px 줄)
    const [refreshing, setRefreshing] = useState(true);
    const [revealTyping, setRevealTyping] = useState(null);
    // 말이 막 보인 뒤 0.4초는 「입력 중」을 숨김(31.1)
    const [typingHeld, setTypingHeld] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [typingMemberKey, setTypingMemberKey] = useState(null);
    const [outbox, setOutbox] = useState([]);
    const [worryPosts, setWorryPosts] = useState({});
    const [loadingOlder, setLoadingOlder] = useState(false);

    const lastIdRef = useRef(cached?.messages.at(-1)?.roomMessagesId ?? 0);
    const revealQueueRef = useRef([]);
    const revealTimerRef = useRef(null);
    const lastRevealAtRef = useRef(0);
    const lastRevealedRef = useRef(null);
    const typingHoldTimerRef = useRef(null);
    const watchUntilRef = useRef(0);
    const generatingRef = useRef(false);
    const pollTimerRef = useRef(null);
    const onArriveRef = useRef(onArrive);
    onArriveRef.current = onArrive;

    const mergeItems = useCallback((items = []) => {
        if (!items.length) {
            return;
        }
        setState((previous) => ({
            ...previous,
            itemsById: { ...previous.itemsById, ...Object.fromEntries(items.map((item) => [item.dailyItemsId, item])) },
        }));
    }, []);

    const commitMessages = useCallback((incoming) => {
        if (!incoming.length) {
            return;
        }
        setState((previous) => ({ ...previous, messages: mergeMessages(previous.messages, incoming) }));
        onArriveRef.current?.(incoming);
    }, []);

    /** 기다리는 멤버 말을 하나 꺼내 보이고, 남았으면 공식 간격 뒤 다음 것. 0.4초 뒤부터 다음 멤버 「입력 중」 */
    const revealNext = useCallback(() => {
        revealTimerRef.current = null;
        const next = revealQueueRef.current.shift();
        if (!next) {
            setRevealTyping(null);
            return;
        }
        commitMessages([next]);
        lastRevealAtRef.current = Date.now();
        lastRevealedRef.current = next;
        setTypingHeld(true);
        clearTimeout(typingHoldTimerRef.current);
        typingHoldTimerRef.current = setTimeout(() => setTypingHeld(false), typingLeadMs(currentRevealSpeed()));
        if (revealQueueRef.current.length) {
            setRevealTyping(revealQueueRef.current[0].memberKey ?? null);
            revealTimerRef.current = setTimeout(revealNext, revealGapOf(next, revealQueueRef.current[0]));
        } else {
            setRevealTyping(null);
        }
    }, [commitMessages]);

    /**
     * 받은 말 넣기. live(폴링으로 막 도착)면 멤버 말은 순차 공개 줄에 세웁니다. 유저 말·처음 불러오기는 바로
     */
    const applyIncoming = useCallback(
        (incoming, { live = false } = {}) => {
            if (!incoming.length) {
                return;
            }
            lastIdRef.current = Math.max(lastIdRef.current, ...incoming.map((message) => message.roomMessagesId));
            if (!live) {
                commitMessages(incoming);
                return;
            }
            const members = incoming.filter((message) => message.sender === 'member' && message.kind === 'text');
            const others = incoming.filter((message) => !members.includes(message));
            commitMessages(others);
            if (!members.length) {
                return;
            }
            revealQueueRef.current.push(...members);
            if (revealTimerRef.current) {
                return;
            }
            const wait =
                lastRevealAtRef.current + revealGapOf(lastRevealedRef.current, revealQueueRef.current[0]) - Date.now();
            if (wait > 0) {
                setRevealTyping(revealQueueRef.current[0].memberKey ?? null);
                revealTimerRef.current = setTimeout(revealNext, wait);
            } else {
                revealNext();
            }
        },
        [commitMessages, revealNext]
    );

    useEffect(
        () => () => {
            clearTimeout(revealTimerRef.current);
            clearTimeout(typingHoldTimerRef.current);
        },
        []
    );

    // 다음에 이 화면을 다시 열 때 바로 그리도록 남겨 둡니다
    useEffect(() => {
        if (cacheKey !== null && state.status === 'ready') {
            // 돌아왔을 때 보이는 것은 그 열기에만(다시 열면 서버가 새로 알려 줌)
            roomCache = { key: cacheKey, state: { ...state, returnVisit: null } };
        }
    }, [cacheKey, state]);

    const setBusy = useCallback((busy, memberKey) => {
        generatingRef.current = busy;
        setGenerating(busy);
        setTypingMemberKey(busy ? (memberKey ?? null) : null);
    }, []);

    const load = useCallback(
        async (signal) => {
            try {
                const data = await roomApi.fetchRoom({ signal });
                noteRevealSpeed(data.revealSpeed);
                lastIdRef.current = data.messages.at(-1)?.roomMessagesId ?? 0;
                let shown = data.messages;
                if (data.firstOpen && !revealQueueRef.current.length) {
                    const entrance = splitEntrance(data.messages);
                    shown = entrance.shown;
                    revealQueueRef.current.push(...entrance.queued);
                }
                setState({
                    status: 'ready',
                    error: null,
                    messages: shown,
                    hasMore: data.hasMore,
                    itemsById: Object.fromEntries((data.dailyItems ?? []).map((item) => [item.dailyItemsId, item])),
                    quiet: data.quiet,
                    hasBirth: data.hasBirth,
                    pushAsk: data.pushAsk ?? null,
                    // 돌아왔을 때 보이는 것(C-20b): 선 · 접힌 묶음 · 소식 카드. 연 때의 마지막 말까지만 접습니다
                    returnVisit: data.returnVisit ?? null,
                    openedLastId: data.messages.at(-1)?.roomMessagesId ?? 0,
                    // 대화 칩(C-29 · D74)
                    chips: data.chips ?? null,
                });
                setRefreshing(false);
                setBusy(data.generating, data.typingMemberKey);
                if (revealQueueRef.current.length && !revealTimerRef.current) {
                    setRevealTyping(revealQueueRef.current[0].memberKey ?? null);
                    revealTimerRef.current = setTimeout(
                        revealNext,
                        revealGapOf(shown.at(-1) ?? null, revealQueueRef.current[0])
                    );
                }
            } catch (error) {
                if (!isAbortError(error)) {
                    setRefreshing(false);
                    setState((previous) => ({
                        ...previous,
                        status: 'error',
                        error: errorMessageOf(error, '별채대화를 불러오지 못했어요.'),
                    }));
                }
            }
        },
        [revealNext, setBusy]
    );

    useEffect(() => {
        const controller = new AbortController();
        load(controller.signal);
        return () => {
            controller.abort();
        };
    }, [load]);

    // 폴링. 한 번 끝나면 다음 간격을 정해 다시 겁니다
    const poll = useCallback(async () => {
        clearTimeout(pollTimerRef.current);
        if (document.visibilityState !== 'hidden') {
            try {
                const data = await roomApi.fetchRoomPending(lastIdRef.current);
                noteRevealSpeed(data.revealSpeed);
                mergeItems(data.dailyItems);
                applyIncoming(data.messages, { live: true });
                // 조용한 리액션(C-20c): 앞 말풍선에 막 붙은 것도 함께 옵니다
                const reactions = data.reactions ?? {};
                if (Object.keys(reactions).length) {
                    setState((previous) => ({
                        ...previous,
                        messages: previous.messages.map((message) =>
                            reactions[message.roomMessagesId]
                                ? { ...message, reactions: reactions[message.roomMessagesId] }
                                : message
                        ),
                    }));
                }
                // 대화 칩: 서버의 지금 묶음으로(없으면 내림). 만드는 중이면 그대로
                if (!data.generating) {
                    setState((previous) =>
                        previous.chips?.chipSetsId === data.chips?.chipSetsId
                            ? previous
                            : { ...previous, chips: data.chips ?? null }
                    );
                }
                setBusy(data.generating, data.typingMemberKey);
            } catch {
                // 다음 차례에 다시 묻습니다
            }
        }
        let delay = IDLE_POLL_MS;
        if (generatingRef.current) {
            delay = FAST_POLL_MS;
        } else if (Date.now() < watchUntilRef.current) {
            delay = WATCH_POLL_MS;
        }
        pollTimerRef.current = setTimeout(poll, delay);
    }, [applyIncoming, mergeItems, setBusy]);

    useEffect(() => {
        if (state.status !== 'ready') {
            return undefined;
        }
        pollTimerRef.current = setTimeout(poll, generatingRef.current ? FAST_POLL_MS : IDLE_POLL_MS);
        function handleVisible() {
            if (document.visibilityState === 'visible') {
                poll();
            }
        }
        document.addEventListener('visibilitychange', handleVisible);
        return () => {
            clearTimeout(pollTimerRef.current);
            document.removeEventListener('visibilitychange', handleVisible);
        };
    }, [state.status, poll]);

    const pollSoon = useCallback(() => {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = setTimeout(poll, 300);
    }, [poll]);

    // 고민 카드의 스레드 상태
    const worryIds = state.messages
        .filter((message) => message.kind === 'worry_card' && message.postsId)
        .map((message) => message.postsId)
        .join(',');
    useEffect(() => {
        if (!worryIds) {
            return undefined;
        }
        let timer = null;
        let stopped = false;
        async function refresh() {
            const ids = worryIds.split(',').map(Number);
            const results = await Promise.allSettled(ids.map((id) => boardApi.fetchPost(id)));
            if (stopped) {
                return;
            }
            const next = {};
            results.forEach((result, index) => {
                if (result.status === 'fulfilled') {
                    next[ids[index]] = toFeedPost(result.value.post);
                }
            });
            setWorryPosts((previous) => ({ ...previous, ...next }));
            if (Object.values(next).some((post) => post.status === 'waiting')) {
                timer = setTimeout(refresh, WORRY_POLL_MS);
            }
        }
        refresh();
        return () => {
            stopped = true;
            clearTimeout(timer);
        };
    }, [worryIds]);

    const deliver = useCallback(
        async (pending) => {
            setOutbox((previous) =>
                previous.map((item) => (item.clientKey === pending.clientKey ? { ...item, status: 'sending' } : item))
            );
            try {
                const { message } = await pending.request();
                setOutbox((previous) => previous.filter((item) => item.clientKey !== pending.clientKey));
                // 내가 말했으니 열린 부르기(고민 제안 칩 말고)는 답한 것입니다. 서버도 같이 표시합니다
                setState((previous) => ({
                    ...previous,
                    messages: previous.messages.map((item) =>
                        item.hook && !item.hook.answered && item.hook.kind !== 'worry'
                            ? { ...item, hook: { ...item.hook, answered: true } }
                            : item
                    ),
                }));
                applyIncoming([message]);
                // 보내자마자 「입력 중」. 첫 멤버를 아직 모르면 이모 (D1a)
                setBusy(true, DEFAULT_TYPING_MEMBER);
                pollSoon();
            } catch (error) {
                // 칩이 바뀐 뒤 누른 칩(23.4): 보내지 않고 새 묶음을 보입니다
                if (error?.code === 'CHIP_STALE') {
                    setOutbox((previous) => previous.filter((item) => item.clientKey !== pending.clientKey));
                    setState((previous) => ({ ...previous, chips: error.details?.chips ?? null }));
                    return;
                }
                setOutbox((previous) =>
                    previous.map((item) =>
                        item.clientKey === pending.clientKey ? { ...item, status: 'failed' } : item
                    )
                );
            }
        },
        [applyIncoming, pollSoon, setBusy]
    );

    const send = useCallback(
        (text, { chipItemsId = null } = {}) => {
            const clientKey = newClientKey();
            const pending = {
                clientKey,
                body: text,
                status: 'sending',
                createdAt: new Date().toISOString(),
                request: () => roomApi.postRoomMessage({ body: text, clientKey, chipItemsId }),
            };
            // 보내면 칩 줄은 바로 꺼집니다(23.7, 새 묶음은 서버가 다음 턴 뒤에)
            setState((previous) => ({ ...previous, chips: null }));
            setOutbox((previous) => [...previous, pending]);
            deliver(pending);
        },
        [deliver]
    );

    const retry = useCallback(
        (clientKey) => {
            const pending = outbox.find((item) => item.clientKey === clientKey);
            if (pending) {
                deliver(pending);
            }
        },
        [deliver, outbox]
    );

    /** 못 보낸 내 말 지우기 (D1d) */
    const discard = useCallback((clientKey) => {
        setOutbox((previous) => previous.filter((item) => item.clientKey !== clientKey));
    }, []);

    const markHookAnswered = useCallback((roomMessagesId) => {
        setState((previous) => ({
            ...previous,
            messages: previous.messages.map((message) =>
                message.roomMessagesId === roomMessagesId && message.hook
                    ? { ...message, hook: { ...message.hook, answered: true } }
                    : message
            ),
        }));
    }, []);

    /** 부르기 칩 · 고민 제안 칩 */
    const answerHook = useCallback(
        async (hookMessage, choice = null) => {
            markHookAnswered(hookMessage.roomMessagesId);
            try {
                const result = await roomApi.answerRoomHook(hookMessage.roomMessagesId, {
                    choice,
                    clientKey: choice ? newClientKey() : null,
                });
                if (result.message) {
                    applyIncoming([result.message]);
                }
                if (hookMessage.hook.kind === 'worry') {
                    watchUntilRef.current = Date.now() + WATCH_AFTER_WORRY_MS;
                } else {
                    setBusy(true, null);
                }
                pollSoon();
                return result;
            } catch (error) {
                setState((previous) => ({
                    ...previous,
                    messages: previous.messages.map((message) =>
                        message.roomMessagesId === hookMessage.roomMessagesId
                            ? { ...message, hook: { ...message.hook, answered: false } }
                            : message
                    ),
                }));
                throw error;
            }
        },
        [applyIncoming, markHookAnswered, pollSoon, setBusy]
    );

    const loadOlder = useCallback(async () => {
        const first = state.messages[0];
        if (!first || !state.hasMore || loadingOlder) {
            return;
        }
        setLoadingOlder(true);
        try {
            const data = await roomApi.fetchRoom({ before: first.roomMessagesId });
            mergeItems(data.dailyItems);
            setState((previous) => ({
                ...previous,
                messages: mergeMessages(previous.messages, data.messages),
                hasMore: data.hasMore,
            }));
        } catch {
            // 위로 다시 올리면 또 묻습니다
        } finally {
            setLoadingOlder(false);
        }
    }, [loadingOlder, mergeItems, state.hasMore, state.messages]);

    const react = useCallback(async (item, kind) => {
        const update = (reactions) =>
            setState((previous) => ({
                ...previous,
                itemsById: { ...previous.itemsById, [item.dailyItemsId]: { ...item, reactions } },
            }));
        update(toggledReactions(item.reactions, kind));
        try {
            const result = await boardApi.toggleReaction(item.dailyItemsId, kind);
            update(result.reactions);
        } catch {
            update(item.reactions);
        }
    }, []);

    /** 별채가 조용할 때 화제 하나(C-25 · D68). 안 던지면 아무 일 없음 */
    const nudge = useCallback(async () => {
        try {
            const { message } = await roomApi.postRoomNudge();
            if (message) {
                applyIncoming([message]);
            }
        } catch {
            // 보조 기능입니다. 실패해도 대화는 그대로
        }
    }, [applyIncoming]);

    return {
        ...state,
        generating,
        // 순차 공개 중이면 다음 멤버, 아니면 서버가 알려 준 지금 쓰는 멤버
        typingMemberKey: typingHeld ? null : (revealTyping ?? typingMemberKey),
        refreshing,
        outbox,
        worryPosts,
        loadingOlder,
        reload: load,
        send,
        retry,
        discard,
        answerHook,
        loadOlder,
        react,
        nudge,
    };
}
